import React from 'react';
import { Link } from 'react-router-dom';
import { LayoutGrid, Plus, Globe, Gift, ArrowRight, Settings, Phone, Mail, Lock, Bell, Folder, FolderOpen, Pencil, Trash2, Ban, LogOut } from 'lucide-react';
import { useLanguage } from '../i18nContext';
import { FolderWithExpanded } from '../hooks/useFolders';
import MembershipStatus from './MembershipStatus';
import BrandLogo from './BrandLogo';

interface DashboardSidebarProps {
    activeTab: string;
    onTabChange: (tab: string) => void;
    folders: FolderWithExpanded[];
    onCreateFolder: () => void;
    onToggleFolder: (id: string) => void;
    editingFolderId: string | null;
    editingFolderName: string;
    setEditingFolderName: (name: string) => void;
    saveEditingFolder: () => void;
    cancelEditingFolder: () => void;
    startEditingFolder: (id: string, name: string) => void;
    folderInputRef: React.RefObject<HTMLInputElement>;
    displayFolderName: (name: string) => string;

    draggingTaskId: string | null;
    dragOverFolderId: string | null;
    handleDragOverFolder: (e: React.DragEvent, folderId: string) => void;
    handleDragLeaveFolder: () => void;
    handleDropToFolder: (e: React.DragEvent, folderId: string) => void;

    setDeleteState: (state: any) => void;

    onLogout: () => void;
    onUpgrade: () => void;
    onOpenInvite: () => void;

    userInfo: { username?: string; email?: string; phone?: string } | null;
    showSettingsPopup: boolean;
    setShowSettingsPopup: (show: boolean) => void;
    setShowChangePassword: (show: boolean) => void;
    setShowBindModal: (type: 'phone' | 'email' | null) => void;
    setShowNotificationModal: (show: boolean) => void;

    onOpenWorkspace: (taskId: string) => void;
    handleDragStart: (e: React.DragEvent, taskId: string) => void;
    handleDragEnd: () => void;
    handleToggleFavorite: (taskId: string, favorite: boolean) => void;
    membershipRefreshKey?: number;
}

export default function DashboardSidebar({
    activeTab, onTabChange, folders, onCreateFolder, onToggleFolder,
    editingFolderId, editingFolderName, setEditingFolderName, saveEditingFolder, cancelEditingFolder, startEditingFolder, folderInputRef, displayFolderName,
    draggingTaskId, dragOverFolderId, handleDragOverFolder, handleDragLeaveFolder, handleDropToFolder,
    setDeleteState, onLogout, onUpgrade, onOpenInvite,
    userInfo, showSettingsPopup, setShowSettingsPopup, setShowChangePassword, setShowBindModal, setShowNotificationModal,
    onOpenWorkspace, handleDragStart, handleDragEnd, handleToggleFavorite, membershipRefreshKey
}: DashboardSidebarProps) {
    const { t, language, setLanguage } = useLanguage();

    return (
        <aside className="w-72 bg-surface-light border-r border-surface-border flex flex-col fixed inset-y-0 left-0 z-30">
            <div className="h-20 flex flex-col justify-center px-6 border-b border-surface-divider/50 bg-white/50 backdrop-blur-sm mb-2">
                <Link to="/" className="inline-block transition-opacity hover:opacity-80">
                    <BrandLogo variant="dark" />
                </Link>
            </div>

            <div className="flex-1 overflow-y-auto py-4 px-4 space-y-1">
                <div className="px-3 mb-3 text-[10px] font-bold text-ink-muted uppercase tracking-widest font-sans">{t.dashboard.main}</div>
                <NavItem icon={<LayoutGrid size={18} />} label={t.dashboard.allRecordings} active={activeTab === 'all'} onClick={() => onTabChange('all')} />
                <NavItem icon={<StarIcon />} label={t.dashboard.favorites} active={activeTab === 'favorites'} onClick={() => onTabChange('favorites')} />
                <div className="flex items-center justify-between px-3 mt-10 mb-3 group">
                    <span className="text-[10px] font-bold text-ink-muted uppercase tracking-widest font-sans">{t.dashboard.folders}</span>
                    <button onClick={onCreateFolder} className="p-1 rounded-md text-ink-muted hover:bg-surface hover:text-ink-dark transition-all">
                        <Plus size={14} />
                    </button>
                </div>
                <div className="space-y-0.5">
                    {folders.map(folder => {
                        const isTaskAlreadyInFolder = draggingTaskId && folder.tasks?.some(t => t.taskId === draggingTaskId);
                        const isDragOverThis = dragOverFolderId === folder.folderId;
                        return (
                            <div key={folder.folderId}>
                                <div className="group relative">
                                    <div
                                        className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl transition-all duration-200 mb-1 cursor-pointer ${activeTab === 'folder_' + folder.folderId
                                            ? 'bg-black/[0.04] text-brand-900 font-bold'
                                            : 'text-ink-secondary hover:text-brand-900 hover:bg-surface font-medium'
                                            } ${isDragOverThis && isTaskAlreadyInFolder ? 'bg-red-50 ring-2 ring-red-300 cursor-not-allowed' : isDragOverThis ? 'bg-surface-border ring-2 ring-brand-700' : ''}`}
                                        onClick={() => { onTabChange('folder_' + folder.folderId); if (!folder.expanded) onToggleFolder(folder.folderId); }}
                                        onDragOver={(e) => handleDragOverFolder(e, folder.folderId)}
                                        onDragLeave={handleDragLeaveFolder}
                                        onDrop={(e) => handleDropToFolder(e, folder.folderId)}
                                    >
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onToggleFolder(folder.folderId); }}
                                            className="p-0.5 rounded hover:bg-surface-border transition-all shrink-0"
                                        >
                                            <span className={`block transition-transform duration-200 ${folder.expanded ? 'rotate-90' : ''}`}>
                                                <ChevronRight className="text-ink-muted" />
                                            </span>
                                        </button>
                                        <span className={activeTab === 'folder_' + folder.folderId ? 'text-brand-900' : 'text-ink-muted'}>
                                            {activeTab === 'folder_' + folder.folderId || folder.expanded ? <FolderOpen size={18} className="fill-surface-border" /> : <Folder size={18} />}
                                        </span>
                                        {editingFolderId === folder.folderId ? (
                                            <input
                                                ref={folderInputRef}
                                                type="text"
                                                value={editingFolderName}
                                                onChange={(e) => setEditingFolderName(e.target.value)}
                                                onBlur={saveEditingFolder}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') saveEditingFolder();
                                                    if (e.key === 'Escape') cancelEditingFolder();
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                className="text-sm font-medium bg-white border border-surface-divider rounded px-1 py-0.5 outline-none focus:ring-1 focus:ring-brand-700 flex-1 min-w-0"
                                            />
                                        ) : (
                                            <span className="text-sm tracking-wide truncate flex-1">{displayFolderName(folder.name)}</span>
                                        )}
                                        {isDragOverThis && isTaskAlreadyInFolder ? (
                                            <Ban size={14} className="text-red-400 ml-auto shrink-0" />
                                        ) : (
                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all ml-auto">
                                                <div
                                                    onClick={(e) => { e.stopPropagation(); startEditingFolder(folder.folderId, folder.name); }}
                                                    className="p-1 hover:bg-surface-border hover:text-brand-900 rounded transition-all cursor-pointer"
                                                >
                                                    <Pencil size={12} />
                                                </div>
                                                <div
                                                    onClick={(e) => { e.stopPropagation(); setDeleteState({ isOpen: true, type: 'Folder', id: folder.folderId, title: displayFolderName(folder.name) }); }}
                                                    className="p-1 hover:bg-rose-100 hover:text-red-500 rounded transition-all cursor-pointer"
                                                >
                                                    <Trash2 size={12} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {/* 展开后显示文件夹内的任务 */}
                                {folder.expanded && (
                                    <div className="ml-7 mt-0.5 mb-1 space-y-1">
                                        {folder.tasksLoading ? (
                                            <div className="flex items-center gap-2 px-3 py-2 text-xs text-ink-muted">
                                                <div className="w-3 h-3 text-ink-muted animate-spin border-2 border-t-ink-muted rounded-full"></div>
                                                <span>Loading...</span>
                                            </div>
                                        ) : folder.tasks && folder.tasks.length > 0 ? (
                                            folder.tasks.map(task => (
                                                <div
                                                    key={task.taskId}
                                                    draggable={true}
                                                    onDragStart={(e) => {
                                                        e.stopPropagation();
                                                        handleDragStart(e, task.taskId);
                                                    }}
                                                    onDragEnd={handleDragEnd}
                                                    onClick={() => onOpenWorkspace(task.taskId)}
                                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-black/[0.04] cursor-grab active:cursor-grabbing transition-all group/task ${draggingTaskId === task.taskId ? 'opacity-50' : ''}`}
                                                >
                                                    <GripVertical className="text-ink-muted shrink-0 opacity-0 group-hover/task:opacity-100" />
                                                    <span className="text-xs font-medium text-brand-900 truncate flex-1">{task.title || (language === 'zh' ? '未命名' : 'Untitled')}</span>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleToggleFavorite(task.taskId, !!task.isFavorite); }}
                                                        className={`p-1 rounded transition-all ${task.isFavorite ? 'text-yellow-500' : 'text-ink-muted opacity-0 group-hover/task:opacity-100 hover:text-yellow-500'}`}
                                                    >
                                                        <StarIcon filled={task.isFavorite ? 1 : 0} />
                                                    </button>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="px-3 py-2 text-xs text-ink-muted italic">
                                                {t.dashboard.noTasksInFolder}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div >

            <div className="p-4 border-t border-surface-border space-y-3 bg-surface-light mb-2">
                <MembershipStatus onUpgrade={onUpgrade} onViewPlan={onUpgrade} refreshTrigger={membershipRefreshKey} />

                <button onClick={onOpenInvite} className="w-full group relative rounded-2xl transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98] overflow-hidden bg-gradient-to-br from-orange-50/50 to-orange-100/50 border border-orange-200/50 hover:border-orange-300/60 shadow-sm hover:shadow-orange-500/10">
                    <div className="relative px-4 py-3 flex items-center gap-3">
                        <div className="relative w-9 h-9 shrink-0 flex items-center justify-center rounded-xl bg-white shadow-md shadow-orange-500/10 group-hover:shadow-orange-500/20 transition-shadow duration-500">
                            <Gift size={18} strokeWidth={2} className="text-orange-500" />
                        </div>
                        <div className="text-left flex-1 min-w-0">
                            <div className="text-sm font-bold text-amber-950 tracking-tight">{t.dashboard.invite}</div>
                            <div className="text-[11px] font-medium text-orange-600/80 uppercase tracking-wider">{t.dashboard.inviteSub}</div>
                        </div>
                        <div className="w-6 h-6 rounded-full bg-white/60 flex items-center justify-center text-orange-500 opacity-0 group-hover:opacity-100 group-hover:translate-x-0 -translate-x-1 transition-all duration-300">
                            <ArrowRight size={12} strokeWidth={2.5} />
                        </div>
                    </div>
                </button>

                <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border border-surface-border transition-colors">
                    <div className="relative settings-popup-container">
                        <button
                            onClick={(e) => { e.stopPropagation(); setShowSettingsPopup(!showSettingsPopup); }}
                            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-all bg-surface hover:bg-black/[0.04] border border-surface-divider hover:border-surface-border group/gear"
                        >
                            <Settings size={18} className="text-ink-secondary group-hover/gear:text-brand-900 group-hover/gear:rotate-90 transition-all duration-500" />
                        </button>

                        {showSettingsPopup && (
                            <div className="absolute bottom-full left-0 mb-3 w-56 bg-white border border-surface-border rounded-2xl shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150 p-1">
                                <div
                                    onClick={() => { setLanguage(language === 'en' ? 'zh' : 'en'); setShowSettingsPopup(false); }}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-colors text-left text-brand-900 hover:bg-surface cursor-pointer"
                                >
                                    <Globe size={16} className="text-ink-muted" />
                                    {language === 'en' ? 'Switch to 中文' : '切换到 English'}
                                </div>
                                <div className="h-px bg-surface-border mx-2" />
                                <div
                                    onClick={() => { if (!userInfo?.phone) { setShowBindModal('phone'); setShowSettingsPopup(false); } }}
                                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-colors text-left ${userInfo?.phone
                                        ? 'text-ink-muted bg-surface'
                                        : 'text-brand-900 hover:bg-surface cursor-pointer'
                                        }`}
                                >
                                    <Phone size={16} className="text-ink-muted" />
                                    {language === 'zh' ? '绑定手机号' : 'Bind Phone'}
                                    {userInfo?.phone && <span className="ml-auto text-xs text-green-500">✓</span>}
                                </div>
                                <div
                                    onClick={() => { if (!userInfo?.email) { setShowBindModal('email'); setShowSettingsPopup(false); } }}
                                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-colors text-left ${userInfo?.email
                                        ? 'text-ink-muted bg-surface'
                                        : 'text-brand-900 hover:bg-surface cursor-pointer'
                                        }`}
                                >
                                    <Mail size={16} className="text-ink-muted" />
                                    {language === 'zh' ? '绑定邮箱' : 'Bind Email'}
                                    {userInfo?.email && <span className="ml-auto text-xs text-green-500">✓</span>}
                                </div>
                                <div className="h-px bg-surface-border mx-2" />
                                <div
                                    onClick={() => {
                                        if ('Notification' in window && Notification.permission === 'granted') return;
                                        setShowNotificationModal(true);
                                        setShowSettingsPopup(false);
                                    }}
                                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-colors text-left ${'Notification' in window && Notification.permission === 'granted'
                                        ? 'text-ink-muted bg-surface'
                                        : 'text-brand-900 hover:bg-surface cursor-pointer'
                                        }`}
                                >
                                    <Bell size={16} className="text-ink-muted" />
                                    {language === 'zh' ? '任务通知' : 'Notifications'}
                                    {'Notification' in window && Notification.permission === 'granted' && <span className="ml-auto text-xs text-green-500">✓</span>}
                                </div>
                                <div className="h-px bg-surface-border mx-2" />
                                <div
                                    onClick={() => { setShowChangePassword(true); setShowSettingsPopup(false); }}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-colors text-left text-brand-900 hover:bg-surface cursor-pointer"
                                >
                                    <Lock size={16} className="text-ink-muted" />
                                    {language === 'zh' ? '修改密码' : 'Change Password'}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex-1 min-w-0 mx-1">
                        <div className="text-sm font-bold text-brand-900 truncate">{userInfo?.username || (language === 'zh' ? '用户' : 'User')}</div>
                        <div className="text-[11px] text-ink-muted truncate font-medium">{userInfo?.email || userInfo?.phone || ''}</div>
                    </div>

                    <button onClick={onLogout} className="p-1.5 rounded-xl flex items-center justify-center shrink-0 hover:bg-black/[0.04] transition-all group">
                        <LogOut size={16} className="text-ink-secondary group-hover:text-red-500" />
                    </button>
                </div>
            </div>
        </aside >
    );
}

function NavItem({ icon, label, active = false, onClick }: { icon: React.ReactNode, label: string, active?: boolean, onClick?: () => void }) {
    return (
        <button onClick={onClick} className={`w-full flex items-center justify-start gap-3 px-3 py-2.5 rounded-xl transition-all font-medium text-sm group ${active
            ? 'bg-black/[0.04] text-brand-900'
            : 'text-ink-secondary hover:bg-surface-hover hover:text-brand-900'
            }`}>
            <span className={active ? 'text-brand-900' : 'text-ink-muted'}>{icon}</span>
            <span className="text-sm tracking-wide">{label}</span>
        </button>
    );
}
function StarIcon({ filled }: { filled?: number }) { return <svg width="18" height="18" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>; }
function GripVertical(props: any) { return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx="9" cy="12" r="1" /><circle cx="9" cy="5" r="1" /><circle cx="9" cy="19" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="5" r="1" /><circle cx="15" cy="19" r="1" /></svg>; }
function ChevronRight(props: any) { return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><polyline points="9 18 15 12 9 6"></polyline></svg>; }
